"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useSelection = void 0;
const react_1 = require("react");
const hooks_1 = require("../../../hooks");
const /**
   * Calculate if two segments overlap in 1D
   * @param lineA [min, max]
   * @param lineB [min, max]
   */ lineIntersects = (lineA, lineB) => lineA[1] >= lineB[0] && lineB[1] >= lineA[0];
const boxIntersects = (boxA, boxB) => {
    // calculate coordinates of all points
    const boxAProjection = {
        x: [boxA.left, boxA.left + boxA.width],
        y: [boxA.top, boxA.top + boxA.height]
    };
    const boxBProjection = {
        x: [boxB.left, boxB.left + boxB.width],
        y: [boxB.top, boxB.top + boxB.height]
    };
    return (lineIntersects(boxAProjection.x, boxBProjection.x) &&
        lineIntersects(boxAProjection.y, boxBProjection.y));
};
function useSelection({ containerRef, selectableRefs, onSelectedChange, canSelect }) {
    const [state, setState] = (0, react_1.useState)({
        selectedChildren: []
    });
    const getCollidingChildren = (0, react_1.useCallback)((selectionBox, dontCallback = false) => {
        const selectedChildren = [];
        if (selectableRefs.length > 0) {
            selectableRefs.forEach((ref, index) => {
                if (ref.current) {
                    const refBox = ref.current.getBoundingClientRect();
                    const tmpBox = {
                        top: refBox.top - window.scrollY,
                        left: refBox.left - window.scrollX,
                        width: ref.current.clientWidth,
                        height: ref.current.clientHeight
                    };
                    if (boxIntersects(selectionBox, tmpBox)) {
                        selectedChildren.push(index);
                    }
                }
            });
        }
        if (!dontCallback &&
            state.selectedChildren.length !== selectedChildren.length &&
            onSelectedChange) {
            onSelectedChange(selectedChildren);
        }
        return selectedChildren;
    }, [selectableRefs, state, onSelectedChange]);
    const handleMouseMove = (0, react_1.useCallback)((event) => {
        setState(state => {
            if (state.mouseDown && state.startPoint) {
                const endPoint = {
                    x: event.x,
                    y: event.y
                };
                const left = Math.min(state.startPoint.x, endPoint.x);
                const top = Math.min(state.startPoint.y, endPoint.y);
                const width = Math.abs(state.startPoint.x - endPoint.x);
                const height = Math.abs(state.startPoint.y - endPoint.y);
                const box = {
                    left,
                    top,
                    width,
                    height
                };
                const selectedChildren = getCollidingChildren(box);
                return Object.assign(Object.assign({}, state), { endPoint,
                    box,
                    selectedChildren });
            }
            else {
                return state;
            }
        });
    }, [setState, getCollidingChildren]);
    const handleMouseUp = (0, react_1.useCallback)((event) => {
        setState(state => (Object.assign(Object.assign({}, state), { mouseDown: false, isDragging: false, startPoint: undefined, endPoint: undefined, box: undefined, selectedChildren: [] })));
    }, [setState]);
    const handleMouseDown = (0, react_1.useCallback)((event) => {
        const box = {
            left: event.x,
            top: event.y,
            width: 1,
            height: 1
        };
        const selectedChildren = getCollidingChildren(box, true);
        const canContinue = canSelect ? canSelect(selectedChildren) : true;
        if (canContinue) {
            setState(state => {
                return Object.assign(Object.assign({}, state), { endPoint: undefined, startPoint: { x: event.x, y: event.y }, mouseDown: true });
            });
        }
    }, [state, setState, handleMouseMove, handleMouseUp, canSelect]);
    const handleDragStart = (0, react_1.useCallback)((event) => {
        setState(state => (Object.assign(Object.assign({}, state), { isDragging: true, mouseDown: true })));
    }, [setState]);
    const handleDragEnd = (0, react_1.useCallback)((event) => {
        setState(state => {
            return Object.assign(Object.assign({}, state), { isDragging: false });
        });
    }, [setState]);
    (0, hooks_1.useEventListener)('dragend', handleDragEnd, document);
    (0, hooks_1.useEventListener)('dragstart', handleDragStart, document);
    (0, hooks_1.useEventListener)('mouseup', handleMouseUp, document);
    (0, hooks_1.useEventListener)('mousedown', handleMouseDown, containerRef.current);
    (0, hooks_1.useEventListener)('mousemove', handleMouseMove, document);
    return state;
}
exports.useSelection = useSelection;
//# sourceMappingURL=useSelection.js.map